//Library Imports
import { useEffect } from "react";
import { useDrag, DragSourceMonitor } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import styled from "styled-components";
import { ItemTypes } from "modules/Common/Components/ItemTypes";
import InfoPopover from "modules/Common/Components/InfoPopover";
import { WFStatus, WFStatusEnum, ControlNameEnum } from "../interfaces";
//Local Imports
import DragIcon from "assets/img/icons/DragIcon.svg";
//Icons Imports
import AddToListIcon from "../../../assets/img/icons/addToListIcon.svg";
import ConditionIcon from "../../../assets/img/icons/conditionIcon.svg";
import DelayIcon from "../../../assets/img/icons/delayIcon.svg";
import EmailIcon from "../../../assets/img/icons/emailIcon.svg";
import StopIcon from "../../../assets/img/icons/stopIcon.svg";
//Style Imports

type Props = {
  controlId: number;
  controlType: string;
  name: string;
  title: string;
  preview?: boolean;
  status?: WFStatus;
  isNodeCopied: boolean;
};

type controlIcons = {
  [key: string]: string;
};

export const endConstraint = (isError?: boolean) => (
  <p
    style={{ marginBottom: 0, fontWeight: 400, fontSize: "16px", fontFamily: "Roboto", color: "rgb(15, 15, 15)" }}
  >
    {isError
      ? "Please remove errors from your workflow before you could publish it."
      : "Add Stop this automation to at least one path in the workflow, so to make the Publish button enabled."}
  </p>
);

const Action = (props: Props) => {
  const { controlId, controlType, name, preview, status, isNodeCopied, title } = props;
  const isStatusActive = status && status === WFStatusEnum.ACTIVE;
  const [{ isDragging }, dragRef, dragPreview] = useDrag(
    () => ({
      type: ItemTypes.CARD,
      item: { controlName: name, title, controlType, controlId },
      collect: (monitor: DragSourceMonitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: (monitor: DragSourceMonitor) => {
        // status should not equal to empty string and also should not have the "draft" status
        if (isStatusActive) return false;
        else if (isNodeCopied) return false;
        return true;
      },
    }),
    [isNodeCopied]
  );

  const controlsIcons: controlIcons = {
    "1": EmailIcon,
    "2": AddToListIcon,
    "5": DelayIcon,
    "3": ConditionIcon,
    "6": StopIcon,
  };

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  return (
    <Wrapper role={preview ? "CardPreview" : "Card"} ref={dragRef} isPreview={preview} disable={isStatusActive}>
      <ActionMain>
        <IconAndTitleWrapper>
          <img src={controlsIcons[controlId as keyof controlIcons]} alt="Icon" />
          <Span>{title}</Span>
        </IconAndTitleWrapper>
        {name === ControlNameEnum.END ? (
          <InfoPopover className="stop-icon" infoText={endConstraint()} width={300} placement={"left"} />
        ) : null}
      </ActionMain>
      <img src={DragIcon} alt="" />
    </Wrapper>
  );
};

export default Action;

type WrapperProps = {
  isPreview?: boolean;
  disable?: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  background-color: ${(props) => (props.disable ? "#e4e4e4" : "#ffffff")};
  border-radius: 8px;
  border: 1px solid #e4e4e4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${(props) => (props.disable ? "not-allowed" : "move")};
  padding: 8px 16px;
  // -webkit-user-drag: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  transform: ${(props) => (props.isPreview ? "rotate(-10deg)" : "rotate(0deg)")};
  &:hover {
    background-color: #e4e4e4;
  }
`;

const Span = styled.span`
  color: #001760;
  font-size: 16px;
  font-weight: 500;
  font-family: "Roboto", sans-serif;
  margin-right: 10px;
  margin-left: 9px;
  margin-top: 3px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    font-size: 12px;
  }
`;

const ActionMain = styled.div`
  display: flex;
  p {
    margin-bottom: 0;
  }
  .stop-icon {
    width: 16px;
  }
`;

const IconAndTitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;
